import { Component, ChangeDetectionStrategy, input, output, signal, computed, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import {
  securityCheckOutlineIcon,
  clockIcon,
  saveIcon,
  gearIcon,
  xIcon,
  infoCircleIcon,
  exclamationCircleIcon,
  userIcon,
} from '@progress/kendo-svg-icons';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { MxdrFormData } from 'src/app/core/models/customer';
import { MxdrFormService } from './services/form.service';

@Component({
  selector: 'app-mxdr-service',
  standalone: true,
  templateUrl: './mxdr-service.component.html',
  styleUrls: ['./mxdr-service.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MxdrFormService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputsModule,
    DropDownsModule,
    DateInputsModule,
    LabelModule,
    ButtonsModule,
    IconsModule,
    SwitchModule,
  ],
})
export class MxdrServiceComponent extends BaseComponent {
  private formService = inject(MxdrFormService);

  // Input/Output - simplified
  public initialData = input<MxdrFormData | null>(null); // Optional initial data for editing
  public isNarrowLayout = input<boolean>(false);
  public dataUpdate = output<MxdrFormData>();
  public validityUpdate = output<boolean>();

  // Kendo UI SVG Icons
  public shieldCheckIcon: SVGIcon = securityCheckOutlineIcon;
  public clockIcon: SVGIcon = clockIcon;
  public serverIcon: SVGIcon = saveIcon;
  public gearIcon: SVGIcon = gearIcon;
  public boxIcon: SVGIcon = xIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public exclamationTriangleIcon: SVGIcon = exclamationCircleIcon;
  public userIcon: SVGIcon = userIcon;

  // Form - self-managed
  public mxdrForm = this.formService.createMxdrForm();

  // Available options
  public xdrServiceOptions = this.formService.getServiceOptions();
  public applianceModelOptions = this.formService.getApplianceModelOptions();
  public virtualOSOptions = this.formService.getVirtualOSOptions();
  public stateOptions = this.formService.getStateOptions();

  // Dynamic delivery options based on selected service
  public xdrServiceDeliveryOptions = computed(() => {
    const selectedService = this.selectedService();
    if (selectedService) {
      return this.formService.getXdrServiceDeliveryOptionsForService(selectedService);
    }
    return this.formService.getXdrServiceDeliveryOptions();
  });

  // Form validity signal
  public isFormValid = signal<boolean>(false);
  public selectedService = signal<string | null>(null);
  public showStandardGovCloudFields = computed(
    () => this.selectedService() === 'xdr-standard' || this.selectedService() === 'xdr-govcloud'
  );
  public showCmmcFields = computed(() => this.selectedService() === 'xdr-cmmc');

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize with existing data if available
    const initialData = this.initialData();
    if (initialData) {
      this.mxdrForm = this.formService.createMxdrForm(initialData);

      // Apply conditional validation if service is already selected
      if (initialData.selectedService) {
        this.selectedService.set(initialData.selectedService);
      }
    }

    // Watch for form changes and emit updates
    this.mxdrForm.valueChanges.subscribe((value) => {
      const formData = this.formService.getFormData(this.mxdrForm);
      this.dataUpdate.emit(formData);

      // Update validity
      const isValid = this.mxdrForm.valid;
      this.isFormValid.set(isValid);
      this.validityUpdate.emit(isValid);
    });

    // Emit initial validity state
    this.validityUpdate.emit(this.mxdrForm.valid);
  }

  public selectService(serviceValue: string): void {
    this.formService.selectService(this.mxdrForm, serviceValue);
    this.selectedService.set(serviceValue);
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.mxdrForm.get('selectedService')?.value === serviceValue;
  }

  public getServiceDescription(serviceValue: string): string {
    return this.formService.getServiceDescription(serviceValue);
  }

  public hasAppliances = computed(() => {
    const appliancesValue = this.mxdrForm.get('appliances')?.value;
    return (appliancesValue as number) > 0;
  });

  public onTotalMachinesChange(value: number | null): void {
    this.formService.onTotalMachinesChange(this.mxdrForm, value);
  }

  public onTotalDailyIngestionChange(value: number | null): void {
    this.formService.onTotalDailyIngestionChange(this.mxdrForm, value);
  }

  // Debug method - call this from browser console to check form state
  public debugFormState(): void {
    this.formService.debugFormState(this.mxdrForm);
  }
}
